import React, { useMemo, useRef, useState, useEffect } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { CHART_TYPES } from '../constants';
import EmptyState from '../Empty/EmptyState';
import LoadingState from '../Loadinng/LoadingState';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

const AVG_BOLD_DIGIT_WIDTH_RATIO = 0.62; // bold system-ui digit ≈ 0.6-0.65em
const FILL_TARGET = 0.82; // leaves breathing room instead of exactly filling the width
const WIDTH_PADDING = 16;
const MIN_FONT_SIZE = 28;
const MAX_FONT_SIZE = 72;
// Safe mid-range value for the single frame before the first ResizeObserver
// measurement arrives, so that frame is never jarringly tiny or huge.
const DEFAULT_FONT_SIZE = 40;

const NumberChart = ({ chartData, isLoading, chartTitle, onNumberClick }) => {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({ width, height });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const total = useMemo(
    () =>
      chartData?.reduce(
        (result, dataSlice) => result + Number.parseInt(dataSlice?.value),
        0,
      ),
    [chartData],
  );

  const formattedTotal = useMemo(
    () => (total === undefined || total === null || isNaN(total) ? '' : total.toLocaleString()),
    [total],
  );

  const fontSize = useMemo(() => {
    const { width, height } = containerSize;
    if (!width || !height || !formattedTotal) return DEFAULT_FONT_SIZE;

    const availableWidth = Math.max(0, width - WIDTH_PADDING);
    const widthDriven =
      (availableWidth * FILL_TARGET) /
      (formattedTotal.length * AVG_BOLD_DIGIT_WIDTH_RATIO);
    const heightDriven = height * 0.42;

    return Math.max(
      MIN_FONT_SIZE,
      Math.min(MAX_FONT_SIZE, Math.floor(Math.min(widthDriven, heightDriven))),
    );
  }, [containerSize, formattedTotal]);

  if (isLoading) {
    return <LoadingState type={CHART_TYPES.NUMBER} />;
  }

  if (total === undefined || total === null || isNaN(total)) {
    return <EmptyState type={CHART_TYPES.NUMBER} />;
  }

  const isClickable = typeof onNumberClick === 'function';

  return (
    <div
      ref={containerRef}
      className={`w-full h-full flex flex-col items-center justify-center gap-3 overflow-hidden ${isClickable ? 'cursor-pointer group' : ''}`}
      onClick={isClickable ? onNumberClick : undefined}
    >
      {/* Number */}
      <div className="relative max-w-full">
        <div
          className={`font-bold leading-none transition-colors max-w-full overflow-hidden whitespace-nowrap ${
            isClickable ? 'group-hover:text-indigo-600' : 'text-gray-900'
          }`}
          style={{ fontFamily: FONT_FAMILY, fontSize }}
        >
          {formattedTotal}
        </div>
        {/* Drill-down arrow — ClickUp style */}
        {isClickable && (
          <ArrowUpRight
            size={20}
            className="absolute -top-1 -right-6 text-gray-300 group-hover:text-indigo-500 transition-colors"
          />
        )}
      </div>

      {/* Supporting label below the number */}
      {chartTitle && (
        <div
          className={`text-sm font-semibold tracking-wide transition-colors ${
            isClickable ? 'text-gray-400 group-hover:text-indigo-400' : 'text-gray-400'
          }`}
        >
          {chartTitle}
        </div>
      )}
    </div>
  );
};

export default NumberChart;
